<template>
    <teleport to="body">
      <div class="drawer-overlay" v-if="visible" @click.self="close">
  
        <transition name="slide-left">
          <div class="drawer-content" v-if="visible">
  
            <!-- HEADER -->
            <div class="drawer-header">
              <div>
                <h2 class="drawer-title">Изменить локацию</h2>
                <p class="drawer-subtitle">Ваш город влияет на результаты поиска и рекомендации</p>
              </div>
              <button class="close-button" @click="close">&#10005;</button>
            </div>
  
            <!-- BODY -->
            <div class="drawer-body" ref="containerRef">
  
              <!-- ЛОАДЕР -->
              <div v-if="isLoading" class="loading-overlay">
                <div class="spinner"></div>
              </div>
  
              <!-- СПИСОК ГОРОДОВ -->
              <!-- СПИСОК ГОРОДОВ -->
              <div class="cities-list">
                <div
                  class="region-block"
                  v-for="(region, index) in groupedRegions"
                  :key="region.name"
                >
                  <!-- REGION HEADER -->
                  <div class="region-header">
                    <img
                      v-if="region.items[0]?.flag"
                      :src="region.items[0].flag"
                      class="region-flag"
                      alt="flag"
                    />
                    <p class="region-title">{{ region.name }}</p>
                  </div>
  
                  <!-- CITIES -->
                  <div
                    class="city-item"
                    v-for="city in region.items"
                    :key="city._id"
                    :class="{ selected: selectedCity?._id === city._id }"
                    @click="selectCity(city)"
                  >
                    <span>{{ city.city }}</span>
                  </div>
  
                  <!-- SEPARATOR -->
                  <div
                    v-if="index !== groupedRegions.length - 1"
                    class="region-separator"
                  ></div>
                </div>
              </div>
  
            </div>
  
            <!-- FOOTER -->
            <div class="drawer-footer">
              <button 
                class="save-btn" 
                @click="handleSaveCity" 
                :disabled="!selectedCity || isSaving"
              >
                Сохранить
              </button>
            </div>
  
          </div>
        </transition>
  
        <!-- GLOBAL OVERLAY -->
        <div v-if="isSaving" class="global-busy-overlay">
          <div class="logout-spinner"></div>
          <p class="logout-text">Сохраняем город…</p>
        </div>
  
      </div>
    </teleport>
  </template>
  
  <script setup>
  import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";

  import CitiesService from "../api/CitiesService.js";
  import RegionService from "../utils/RegionService.js";
  import CompanyService from "../api/CompanyService.js";

  const props = defineProps({
    visible: Boolean,
    modelValue: Object,
    companyId: String  
  });
  
  const emit = defineEmits(["close", "update:modelValue"]);
  
  // STATE
  const cities = ref([]);
  const selectedCity = ref(null);
  
  const isLoading = ref(true);
  const isSaving = ref(false);
  
  const containerRef = ref(null);
  const observer = new ResizeObserver(() => {});
  
  // LOAD CITIES
  onMounted(async () => {
    const data = await CitiesService.fetchCities();
  
    // 👇 ДОБАВЛЯЕМ ПОЛЕ flag
    cities.value = data.map(city => ({
      ...city,
      flag: RegionService.getFlagUrl(city.region_id)
    }));
  
    isLoading.value = false;
  
    nextTick(() => {
      if (containerRef.value) observer.observe(containerRef.value);
    });
  });
  
  onUnmounted(() => observer.disconnect());
  
  // GROUP BY REGION
  const groupedCities = computed(() => {
    const groups = {};
  
    cities.value.forEach(city => {
      const region = city.region_name || "Другие регионы";
      if (!groups[region]) groups[region] = [];
      groups[region].push(city);
    });
  
    return groups;
  });
  
  const groupedRegions = computed(() =>
    Object.entries(groupedCities.value).map(([name, items]) => ({
      name,
      items
    }))
  );
  
  // SELECT CITY
  const selectCity = (city) => {
    selectedCity.value = city;
  };
  
  // SAVE CITY
  const handleSaveCity = async () => {
  if (!selectedCity.value) return;

  isSaving.value = true;

  try {
    await CompanyService.updateCompanyRegion(
      props.companyId,
      selectedCity.value.region_id
    );

    emit("update:modelValue", selectedCity.value);
    emit("close");

  } catch (err) {
    console.error("❌ Ошибка обновления региона:", err);
  } finally {
    isSaving.value = false;
  }
};
  
  const close = () => emit("close");
  </script>
  
  
  <style scoped>
  /* ============================== */
  /* OVERLAY */
  /* ============================== */
  
  .drawer-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.45);
    backdrop-filter: blur(4px);
    z-index: 1000;
    display: flex;
  }
  
  /* ============================== */
  /* DRAWER */
  /* ============================== */
  
  .drawer-content {
    width: 580px;
    background: var(--drawer-bg);
    height: 100%;
    display: flex;
    flex-direction: column;
    box-shadow: 4px 0 20px rgba(0,0,0,0.25);
    animation: fadeIn 0.2s ease-out;
  }
  
  /* ============================== */
  /* HEADER */
  /* ============================== */
  
  .drawer-header {
    padding: 28px 32px;
    border-bottom: 1px solid var(--drawer-border);
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }
  
  .drawer-title {
    font-size: 20px;
    font-family: "Eastman-Bold";
    margin-bottom: 4px;
  }
  
  .drawer-subtitle {
    font-size: 13px;
    font-family: "Eastman-Regular";
    opacity: 0.65;
    line-height: 1.3;
  }
  
  .close-button {
    background: none;
    border: none;
    font-size: 20px;
    opacity: 0.7;
    cursor: pointer;
    transition: 0.15s ease;
  }
  
  .close-button:hover {
    opacity: 1;
    transform: scale(1.1);
  }
  
  /* ============================== */
  /* BODY */
  /* ============================== */
  
  .drawer-body {
    padding: 24px 32px;
    overflow-y: auto;
  }
  
  /* REGION HEADER */
  .region-title {
    font-family: "Eastman-Medium";
    font-size: 14px;
    margin-bottom: 10px;
    color: var(--region-title-color);
    letter-spacing: 0.3px;
  }
  
  /* LIST OF CITIES */
  .cities-list {
    display: flex;
    flex-direction: column;
    gap: 36px;
  }
  
  /* CITY BUTTON */
  .city-item {
    padding: 12px 18px;
    border: 1px solid var(--city-border);
    border-radius: 12px;
    cursor: pointer;
    transition: 0.18s ease;
    background: var(--city-bg);
    font-family: "Eastman-Regular";
    font-size: 15px;
  }
  
  .city-item:hover {
    background: var(--city-hover-bg);
    border-color: var(--city-hover-border);
    transform: translateY(-2px);
  }
  
  .city-item.selected {
    background: #2887F7;
    color: white;
    border-color: #2887F7;
    box-shadow: 0 4px 12px rgba(40,135,247,0.35);
  }
  
  /* ============================== */
  /* FOOTER */
  /* ============================== */
  
  .drawer-footer {
    padding: 20px 32px;
    border-top: 1px solid var(--drawer-border);
    display: flex;
    justify-content: flex-end;
  }
  
  .save-btn {
    background: #2887F7;
    padding: 12px 32px;
    border-radius: 26px;
    border: none;
    color: white;
    font-family: "Eastman-Medium";
    font-size: 15px;
    cursor: pointer;
    transition: 0.2s ease;
  }
  
  .save-btn:hover:not(:disabled) {
    background: #1d6ed6;
    transform: translateY(-2px);
  }
  
  .save-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  /* ============================== */
  /* LOADING MASK */
  /* ============================== */
  
  .loading-overlay {
    position: absolute;
    inset: 0;
    background: rgba(255,255,255,0.4);
    backdrop-filter: blur(3px);
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  /* SPINNER */
  .spinner {
    width: 42px;
    height: 42px;
    border: 4px solid rgba(255,255,255,0.35);
    border-top-color: #2887F7;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  /* ============================== */
  /* GLOBAL OVERLAY */
  /* ============================== */
  
  .global-busy-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.75);
    backdrop-filter: blur(6px);
    z-index: 2000;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  
  .logout-spinner {
    width: 50px;
    height: 50px;
    border: 5px solid rgba(255,255,255,0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin .8s linear infinite;
  }
  
  .logout-text {
    color: white;
    font-size: 16px;
    margin-top: 16px;
    opacity: 0;
    animation: fadeIn 0.5s forwards 0.15s;
    font-family: "Eastman-Regular";
  }
  
  /* ============================== */
  /* THEMES */
  /* ============================== */
  
  :root {
    --drawer-bg: #ffffff;
    --drawer-border: #e2e2e2;
  
    --city-bg: #f8f8f8;
    --city-hover-bg: #eef4ff;
  
    --city-border: #e0e0e0;
    --city-hover-border: #2887f7;
  
    --region-title-color: #505050;
  }
  
  [data-theme="dark"] {
    --drawer-bg: #1d1d1d;
    --drawer-border: #333;
  
    --city-bg: #2a2a2a;
    --city-hover-bg: #2f3b55;
  
    --city-border: #444;
    --city-hover-border: #2887f7;
  
    --region-title-color: #cccccc;
  }
  
  /* ============================== */
  /* ANIMATIONS */
  /* ============================== */
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  @keyframes fadeIn {
    to { opacity: 1; }
  }
  </style>
  
  <style>
  .slide-left-enter-active,
  .slide-left-leave-active {
    transition: transform 0.3s ease;
  }
  
  .slide-left-enter-from,
  .slide-left-leave-to {
    transform: translateX(-100%);
  }
  
  .cities-list {
    display: flex;
    flex-direction: column;
    gap: 34px;
  }
  
  /* BLOCK WITH FLAG + REGION NAME */
  .region-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }
  
  .region-flag {
    width: 26px;
    height: 18px;
    border-radius: 3px;
    object-fit: cover;
    box-shadow: 0 0 4px rgba(0,0,0,0.15);
  }
  
  .region-title {
    font-family: "Eastman-Bold";
    font-size: 15px;
    letter-spacing: -0.2px;
    opacity: 0.85;
    color: var(--region-title-color);
  }
  
  /* CITY LIST ITEM */
  .city-item {
    padding: 12px 18px;
    border: 1px solid var(--city-border);
    border-radius: 12px;
    margin-bottom: 8px;
    cursor: pointer;
    transition: 0.18s ease;
    background: var(--city-bg);
    font-family: "Eastman-Regular";
    font-size: 15px;
  }
  
  .city-item:hover {
    background: var(--city-hover-bg);
    border-color: var(--city-hover-border);
    transform: translateY(-2px);
  }
  
  .city-item.selected {
    background: #2887F7;
    color: white;
    border-color: #2887F7;
    box-shadow: 0 4px 14px rgba(40,135,247,0.35);
  }
  
  /* DARK THEME FIXES */
  [data-theme="dark"] .region-flag {
    box-shadow: 0 0 2px rgba(255,255,255,0.2);
  }
  
  .close-button {
    color: #000;
  }
  
  [data-theme="dark"] .close-button {
    color: #fff;
  }
  
  .region-separator {
    height: 1px;
    width: 100%;
    background-color: var(--drawer-border);
    opacity: 0.4;
    margin: 18px 0 6px 0;
  }
  
  </style>